import React, { useState } from 'react';
import {
    Button,
    Text,
    Tooltip,
    makeStyles,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import { Warning20Filled, ArrowDownload20Regular, Checkmark20Filled } from '@fluentui/react-icons';
import { useMsal } from '@azure/msal-react';
import { useAppSelector } from '../../../redux/hooks';
import { useProviderServices } from '../../../app/providers/AppServicesContext';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import { NexusChatSession } from '../../../domain/chat/models/ChatSession';
import { acquireApiAccessTokenAsync } from '../../../services/auth/AuthService';
import { downloadChatSessionHandler } from '../../../application/chat-sessions/handlers/DownloadChatSessionHandler';
import { downloadChatSessionAsText } from '../../utils/downloadChatSessionDocument';
import { customColors } from '../../../styles';
import { useMigration } from '../hooks/useMigration';
import MigrationModal from './MigrationModal';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
        ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
        ...shorthands.borderLeft('4px', 'solid', customColors.protivitiOrange),
        backgroundColor: 'rgba(246, 135, 31, 0.07)',
        gap: tokens.spacingHorizontalM,
    },
    message: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        flexShrink: 1,
        minWidth: 0,
    },
    icon: {
        color: customColors.protivitiOrange,
        flexShrink: 0,
    },
    text: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        flexShrink: 0,
    },
    toolbarButton: {
        height: '36px',
        minWidth: '36px',
        ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),
        ...shorthands.borderRadius('5px'),
        transition: 'all 0.3s ease',
        '& > span > svg': {
            color: customColors.protivitiDarkBlue,
            transition: 'all 0.3s ease',
        },
        ':hover': {
            backgroundColor: customColors.protivitiDarkBlue,
            '& > span > svg': {
                color: customColors.white,
            },
        },
        ':active': {
            backgroundColor: customColors.protivitiOrange,
            ...shorthands.border('1px', 'solid', customColors.protivitiOrange),
            transition: 'all 0s',
        },
    },
    migrateButton: {
        height: '36px',
        backgroundColor: customColors.protivitiDarkBlue,
        color: customColors.white,
        ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),
        ...shorthands.borderRadius('5px'),
        fontWeight: tokens.fontWeightSemibold,
        transition: 'all 0.3s ease',
        ':hover': {
            backgroundColor: customColors.protivitiOrange,
            color: customColors.white,
            opacity: '0.85',
        },
        ':active': {
            backgroundColor: customColors.protivitiOrange,
            ...shorthands.border('1px', 'solid', customColors.protivitiOrange),
            transition: 'all 0s',
        },
    },
    migratedButton: {
        height: '36px',
        backgroundColor: customColors.green,
        color: customColors.white,
        ...shorthands.border('1px', 'solid', customColors.green),
        ...shorthands.borderRadius('5px'),
        fontWeight: tokens.fontWeightSemibold,
        cursor: 'default',
        ':hover': {
            backgroundColor: customColors.protivitiDarkBlue,
            color: customColors.white,
        },
    },
});

interface MigrateButtonProps {
    sessionId: string;
}

const MigrateButton: React.FC<MigrateButtonProps> = ({ sessionId }) => {
    const styles = useStyles();
    const migration = useMigration();
    const [modalOpen, setModalOpen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const { instance } = useMsal();
    const providerServices = useProviderServices();
    const session = useAppSelector((state) => state.chatSessions.byId[sessionId]);
    const isMigrated = useAppSelector((state) => {
        // Primary: localStorage-backed migrationContext (survives navigation and most reloads)
        if (Object.values(state.migrationContext.bySessionId).some((e) => e.migratedFromId === sessionId)) {
            return true;
        }
        // Fallback: session metadata from backend (survives cache clear)
        return Object.values(state.chatSessions.byId).some(
            (s) => s.providerType === ChatProviderType.Nexus &&
                   (s as NexusChatSession).metadata?.migratedFrom === sessionId,
        );
    });

    const handleDownload = async (): Promise<void> => {
        if (!session) return;
        setIsDownloading(true);
        try {
            const accessToken = await acquireApiAccessTokenAsync({ msalInstance: instance });
            const data = await downloadChatSessionHandler({ session, accessToken, providerServices });
            downloadChatSessionAsText(data, session.title);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <>
            <div className={styles.root}>
                <span className={styles.message}>
                    <Warning20Filled className={styles.icon} />
                    <Text className={styles.text}>
                        This chat has been archived. You can download a text file or migrate it to your active chats to continue the conversation.   
                    </Text>
                </span>
                <div className={styles.actions}>
                    <Tooltip content="Download chat" relationship="label">
                        <Button
                            className={styles.toolbarButton}
                            appearance="transparent"
                            icon={<ArrowDownload20Regular />}
                            disabled={isDownloading || !session}
                            aria-label="Download chat"
                            onClick={() => void handleDownload()}
                        />
                    </Tooltip>
                    {isMigrated ? (
                        <Button
                            className={styles.migratedButton}
                            appearance="transparent"
                            icon={<Checkmark20Filled />}
                            disabled
                        >
                            Migrated
                        </Button>
                    ) : (
                        <Button
                            className={styles.migrateButton}
                            appearance="transparent"
                            onClick={() => setModalOpen(true)}
                        >
                            Migrate to Active Chats
                        </Button>
                    )}
                </div>
            </div>
            <MigrationModal
                open={modalOpen}
                sessionId={sessionId}
                migration={migration}
                onDismiss={() => setModalOpen(false)}
            />
        </>
    );
};

export default MigrateButton;
